// src/modules/app-views/products/pages/Ai/components/ProductImageWithFallback.jsx
import { useState, useEffect } from 'react';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';

/**
 * ProductImageWithFallback - Image component with progressive loading and SimpleAvatar fallback
 * Behavior:
 * 1. Show SimpleAvatar with initials immediately
 * 2. If base64Thumb exists, show it with loading indicator
 * 3. Load HQ image in background, then swap when ready
 * 4. On error, fallback to SimpleAvatar with initials
 */
const ProductImageWithFallback = ({ avatar, name, backColor, foreColor }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [currentSrc, setCurrentSrc] = useState('');
  const [isLoadingHQ, setIsLoadingHQ] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Extract image URLs from avatar object
  const base64Thumb = avatar?.base64Thumb;
  const thumbUrl = avatar?.thumbUrl;
  const storageUrl = avatar?.storageUrl || avatar?.url;
  const hqUrl = thumbUrl || storageUrl;

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Reset states when avatar changes
    setImageError(false);
    setCurrentSrc('');
    setIsLoadingHQ(false);

    if (!avatar) return;

    // If we have base64Thumb and a HQ URL, do progressive loading
    if (base64Thumb && hqUrl) {
      // Show low-quality immediately
      setCurrentSrc(base64Thumb);
      setIsLoadingHQ(true);

      // Preload HQ image in background
      const img = new window.Image();
      img.onload = () => {
        setCurrentSrc(hqUrl);
        setIsLoadingHQ(false);
      };
      img.onerror = () => {
        // Keep base64Thumb on error
        setIsLoadingHQ(false);
      };
      img.src = hqUrl;
    } else if (hqUrl) {
      // No base64Thumb, try to load HQ directly
      setIsLoadingHQ(true);
      const img = new window.Image();
      img.onload = () => {
        setCurrentSrc(hqUrl);
        setIsLoadingHQ(false);
      };
      img.onerror = () => {
        setImageError(true);
        setIsLoadingHQ(false);
      };
      img.src = hqUrl;
    } else if (base64Thumb) {
      // Only base64Thumb available
      setCurrentSrc(base64Thumb);
    }
  }, [avatar, base64Thumb, hqUrl]);

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  // Show SimpleAvatar if no image or error
  if (!avatar || imageError || (!currentSrc && !isLoadingHQ)) {
    return (
      <div className="avatar-with-border">
        <SimpleAvatar
          name={name}
          size={150}
          shape="square"
          backColor={backColor}
          foreColor={foreColor}
          style={{ flexShrink: 0 }}
          showShadow={false}
        />
      </div>
    );
  }

  return (
    <div className="avatar-with-border">
      <SimpleAvatar
        src={currentSrc}
        name={name}
        size={150}
        shape="square"
        backColor={backColor}
        foreColor={foreColor}
        loading={isLoadingHQ}
        style={{ flexShrink: 0 }}
        showShadow={false}
      />
    </div>
  );
};

export default ProductImageWithFallback;
